import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, Clock, ShieldCheck, Award, Filter, BookOpen, FileBadge } from "lucide-react";

export default function Catalog() {
  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
  }, []);

  const categories = useMemo(() => {
    const set = new Set(courses.map((c) => c.category).filter(Boolean));
    return ["all", ...Array.from(set).sort()];
  }, [courses]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return courses.filter((c) => {
      if (cat !== "all" && (c.category || "") !== cat) return false;
      if (!s) return true;
      return (c.title + " " + c.description).toLowerCase().includes(s);
    });
  }, [courses, search, cat]);

  return (
    <div>
      {/* STPS Banner */}
      <section className="bg-marino text-white border-b-4 border-naranja">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid md:grid-cols-[auto_1fr] gap-6 items-center">
          <div className="hidden md:flex flex-shrink-0 items-center justify-center w-28 h-28 rounded-2xl bg-white/5 border border-white/15 backdrop-blur-sm">
            <FileBadge className="w-14 h-14 text-naranja" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 bg-naranja/15 border border-naranja/30 text-naranja text-[11px] uppercase tracking-widest font-bold px-3 py-1 rounded-full">
              <ShieldCheck className="w-3.5 h-3.5" /> Constancia oficial DC-3
            </div>
            <h1 className="mt-3 font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-tight">
              Cursos con <span className="text-naranja">Validez Oficial STPS</span>
            </h1>
            <p className="mt-4 text-white/85 text-sm sm:text-base leading-relaxed max-w-3xl">
              Todos nuestros cursos emiten una <strong className="text-white">constancia de acreditación STPS "DC-3"</strong>,
              la cual acredita a una persona en el cumplimiento de capacitación y adquisición de nuevas habilidades para
              ejercerlas en una ocupación laboral dentro de las empresas.
            </p>
            <div className="mt-5 flex flex-wrap gap-2 text-xs">
              <span className="inline-flex items-center gap-1 bg-white/5 border border-white/10 px-2.5 py-1 rounded-md text-white/80"><Award className="w-3.5 h-3.5 text-naranja" /> Certificación oficial</span>
              <span className="inline-flex items-center gap-1 bg-white/5 border border-white/10 px-2.5 py-1 rounded-md text-white/80"><ShieldCheck className="w-3.5 h-3.5 text-naranja" /> Reconocida por empresas</span>
              <span className="inline-flex items-center gap-1 bg-white/5 border border-white/10 px-2.5 py-1 rounded-md text-white/80"><FileBadge className="w-3.5 h-3.5 text-naranja" /> Cumple normativa laboral</span>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8 border-b border-gris/20">
        <div className="grid md:grid-cols-[1fr_auto] gap-3 items-center">
          <div className="relative">
            <Search className="w-4 h-4 text-gris absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="catalog-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre o descripción..."
              className="w-full pl-9 pr-4 py-2.5 border border-gris/50 rounded-lg text-sm focus:border-marino focus:outline-none"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-4 h-4 text-marino/60 hidden sm:block" />
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c}`}
                onClick={() => setCat(c)}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                  cat === c
                    ? "bg-marino text-white border-marino"
                    : "bg-white text-marino/80 border-gris/50 hover:border-marino"
                }`}
              >
                {c === "all" ? "Todas" : c}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-3 text-xs text-marino/60">
          Mostrando <strong className="text-marino">{filtered.length}</strong> curso{filtered.length !== 1 && "s"}{cat !== "all" && ` en "${cat}"`}
        </div>
      </section>

      {/* Cards */}
      <section id="courses" className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="w-12 h-12 text-gris mx-auto mb-3" />
            <p className="text-marino/70">Sin resultados. Ajusta el buscador o los filtros.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((c) => (
              <div key={c.id} data-testid={`catalog-course-${c.id}`} className="bg-white border border-gris/30 rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow group">
                <div className="aspect-video bg-marino/10 relative overflow-hidden">
                  {c.thumbnail && (
                    <img src={c.thumbnail} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  )}
                  <div className="absolute top-3 right-3 bg-marino text-white text-xs font-semibold px-2 py-1 rounded-md">
                    ${c.price}
                  </div>
                  {c.category && (
                    <div className="absolute bottom-3 left-3 bg-naranja text-white text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded">
                      {c.category}
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-heading font-bold text-marino text-lg leading-snug">{c.title}</h3>
                  <p className="mt-2 text-sm text-marino/70 line-clamp-2">{c.description}</p>
                  <div className="mt-4 flex items-center justify-between text-sm text-marino/70">
                    <span className="inline-flex items-center gap-1"><Clock className="w-4 h-4" /> {c.duration_hours}h</span>
                    {c.teacher_name && <span className="text-xs">Instructor {c.teacher_name}</span>}
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Link to={`/courses/${c.id}`} className="flex-1 bg-gris text-marino text-center px-4 py-2 rounded-lg text-sm font-medium hover:bg-grisHover">
                      Ver detalle
                    </Link>
                    <Link to={`/checkout/${c.id}`} className="flex-1 bg-naranja text-white text-center px-4 py-2 rounded-lg text-sm font-medium hover:bg-naranjaHover">
                      Empezar
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
